import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, XCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useWorkspace();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }

    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status) || attempts.current > 10) {
          setStatus("failed");
          return;
        }
      } catch {
        if (attempts.current > 10) { setStatus("failed"); return; }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain p-6">
      <div className="max-w-md w-full text-center rounded-lg border border-border bg-card p-10 relative z-10" data-testid="payment-result">
        {status === "checking" && (
          <>
            <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="font-heading font-bold text-2xl">Confirming payment…</h1>
            <p className="text-muted-foreground mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={56} weight="fill" className="text-chart-2 mx-auto mb-6" />
            <h1 className="font-heading font-black text-2xl">You're upgraded! 🎉</h1>
            <p className="text-muted-foreground mt-2">Your new plan is active. Time to repurpose more content.</p>
            <Button className="mt-8 w-full" data-testid="go-dashboard" onClick={() => navigate("/dashboard")}>Go to dashboard</Button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle size={56} weight="fill" className="text-destructive mx-auto mb-6" />
            <h1 className="font-heading font-bold text-2xl">Payment not completed</h1>
            <p className="text-muted-foreground mt-2">No charge was made. You can try again anytime.</p>
            <Button variant="outline" className="mt-8 w-full" data-testid="back-billing" onClick={() => navigate("/billing")}>Back to billing</Button>
          </>
        )}
      </div>
    </div>
  );
}
